import React, { useMemo, useState, useEffect, useRef } from "react";
import { CANON_SPEC, MAPPING_WHY } from "../utils/canonicalSpecs";

const PREVIEW_ROWS = 5;
const MESSAGE_TYPE_KEY = "Message Type";
const DEFAULT_MESSAGE_TYPES = ["Repost", "Reply", "Mention", "Update"];   // shown to user
const CANON_MESSAGE_TYPES   = DEFAULT_MESSAGE_TYPES.map(v => `X ${v}`);    // saved backend values
const MAX_MSGTYPE_DISTINCT = 8; // >5 means it's likely not a Message Type column

// --- Tiny toast for success ---
function Toast({
  show,
  title = "Mapping saved",
  msg = "Your column mapping has been applied.",
  onClose,
}) {
  if (!show) return null;
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className="rounded-md border border-green-300 bg-green-50 px-4 py-3 shadow">
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 text-green-700 mt-0.5"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10Zm-1.1-6.1-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4-6 6Z"
              fill="currentColor"
            />
          </svg>
          <div className="text-sm">
            <div className="font-medium text-green-900">{title}</div>
            <div className="text-green-800">{msg}</div>
          </div>
          <button
            className="ml-2 text-sm text-green-800 underline"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Small modal wrapper ---
function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="w-full max-w-3xl sm:max-w-4xl bg-white rounded-lg shadow-lg
                        max-h-[85vh] flex flex-col"
        >
          {/* Header (fixed) */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h4 className="text-lg font-semibold text-purple-900">{title}</h4>
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              aria-label="Close modal"
            >
              Close
            </button>
          </div>

          {/* Body (scrolls vertically) */}
          <div className="p-4 overflow-y-auto max-h-[70vh]">{children}</div>
        </div>
      </div>
    </div>
  );
}

// --- Draggable user-column pill ---
function HeaderChip({ name, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, name)}
      className="inline-flex items-center gap-2 px-2 py-1 border rounded bg-white shadow-sm cursor-grab text-sm"
      title={name}
    >
      {name}
    </div>
  );
}

function ErrorCallout({ message, onClose }) {
  if (!message) return null;

  // pull out the first few “row N: "value"” lines if present
  const lines = message.split("\n");
  const exampleIdx = lines.findIndex((l) =>
    l.startsWith("First bad examples:")
  );
  const examples = exampleIdx >= 0 ? lines.slice(exampleIdx + 1) : [];

  return (
    <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
      <div className="flex items-start gap-3">
        <svg className="h-5 w-5 mt-0.5" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" />
          <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" />
          <line x1="12" y1="10" x2="12" y2="16" stroke="currentColor" />
        </svg>
        <div className="flex-1">
          {/* first line is the main message */}
          <div className="font-medium">{lines[0]}</div>
          {/* show the “Accepted:” and any examples if present */}
          <div className="mt-1 whitespace-pre-line">
            {lines
              .slice(1, exampleIdx >= 0 ? exampleIdx : undefined)
              .join("\n")}
          </div>
          {examples.length > 0 && (
            <div className="mt-2">
              <div className="text-xs font-medium">First bad examples</div>
              <ul className="mt-1 list-disc pl-5 text-xs">
                {examples.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <button className="text-sm underline" onClick={onClose}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

// --- Message Type Value Map modal ---
function ValueMapModal({
  open,
  onClose,
  srcCol,
  values,
  displayChoices,     // e.g. ["Repost","Reply","Mention","Update"] (shown)
  canonicalChoices,   // e.g. ["X Repost","X Reply",...] (saved)
  map,
  setMap,
}) {
  if (!open) return null;

  return (
    <Modal
      title={`Map values for "${srcCol}" → Message Type`}
      onClose={onClose}
    >
      <p className="text-sm text-gray-600 mb-3">
        Match each value in your <span className="font-mono">{srcCol}</span>{" "}
        column to a canonical Message Type.
      </p>
      <div className="max-h-[60vh] overflow-y-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left border-b w-1/2">Your value</th>
              <th className="px-3 py-2 text-left border-b">Canonical</th>
            </tr>
          </thead>
          <tbody>
            {values.map((v) => {
              const current = map[v] || "";
              // prevent picking a canonical that's already assigned to a different value
              const usedCanon = new Set(
                Object.entries(map)
                  .filter(([k, val]) => k !== v && !!val)
                  .map(([, val]) => val)
              );

              return (
                <tr key={v} className="odd:bg-white even:bg-gray-50">
                  <td className="px-3 py-2 border-b align-top">
                    {v === "" ? <em>(empty)</em> : v}
                  </td>
                  <td className="px-3 py-2 border-b">
                    <select
                      className="px-2 py-1 border rounded bg-white text-sm"
                      value={current}
                      onChange={(e) => {
                        const canonVal = e.target.value || undefined;
                        setMap((m) => {
                          const next = { ...m };
                          // evict this canonical from any other value to keep uniqueness
                          if (canonVal) {
                            for (const k of Object.keys(next)) {
                              if (k !== v && next[k] === canonVal) {
                                next[k] = undefined;
                              }
                            }
                          }
                          next[v] = canonVal;
                          return next;
                        });
                      }}
                    >
                      <option value="">— select —</option>
                      {displayChoices.map((label, i) => {
                        const canonVal = canonicalChoices[i];
                        // show if unused OR it's the current selection for this row
                        if (!usedCanon.has(canonVal) || canonVal === current) {
                          return (
                            <option key={canonVal} value={canonVal}>
                              {label}
                            </option>
                          );
                        }
                        return null;
                      })}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          className="px-4 py-2 rounded border bg-white hover:bg-gray-50"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </Modal>
  );
}

export default function HeaderMapper({
  preview, // { type, headers, rows }
  onConfirm, // async (mappingSrcToCanon, options?) => void

  // default to full CANON_SPEC when platform-specific keys are not supplied
  required = CANON_SPEC.required,
  optional = CANON_SPEC.optional,

  // NEW: platform-scoped keys
  requiredKeys, // string[] of required keys for selected platform
  optionalKeys, // string[] of optional keys for selected platform
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);

  // --- Backward-compatible platform-aware lists ---
  const platformMode = useMemo(
    () => (Array.isArray(requiredKeys) && requiredKeys.length) || (Array.isArray(optionalKeys) && optionalKeys.length),
    [requiredKeys, optionalKeys]
  );

  const effRequired = useMemo(() => {
    const baseReq = required || CANON_SPEC.required || [];
    const baseOpt = optional || CANON_SPEC.optional || [];
    const allByKey = new Map([...baseReq, ...baseOpt].map(f => [f.key, f]));
    const list =
      Array.isArray(requiredKeys) && requiredKeys.length
        ? requiredKeys
        : baseReq.map(f => f.key);

    // map to spec entries (create simple stubs if a key isn't in CANON_SPEC yet)
    return list.map(k => allByKey.get(k) ?? { key: k, desc: '', example: '' });
  }, [required, optional, requiredKeys]);


  const effOptional = useMemo(() => {
    const baseReq = required || CANON_SPEC.required || [];
    const baseOpt = optional || CANON_SPEC.optional || [];
    const allByKey = new Map([...baseReq, ...baseOpt].map(f => [f.key, f]));
    if (!platformMode) return baseOpt;
    const reqSet = new Set(requiredKeys || []);
    const list = Array.isArray(optionalKeys) ? optionalKeys : [];
    return list
      .filter(k => !reqSet.has(k))
      .map(k => allByKey.get(k) ?? { key: k, desc: '', example: '' });
  }, [ platformMode, required, optional, requiredKeys, optionalKeys]);

  // mapping stored as canon -> src (nice for rendering); convert on confirm
  const [canonToSrc, setCanonToSrc] = useState({});

  // feedback states
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const errRef = useRef(null);

  // --- Message Type sub-mapping state ---
  const [mtOpen, setMtOpen] = useState(false); // modal visible
  const [mtSrc, setMtSrc] = useState(null); // user column mapped to "Message Type"
  const [mtValues, setMtValues] = useState([]); // distinct values from that column
  const [mtMap, setMtMap] = useState({}); // { userValue: canonicalValue }

  // --- Reset mapping when preview/fields change (new file uploaded etc.) ---
  const previewSig = useMemo(
    () => (preview?.headers || []).join("|"),
    [preview?.headers]
  );
  const requiredSig = useMemo(
    () => (effRequired || []).map((f) => f.key).join("|"),
    [effRequired]
  );
  const optionalSig = useMemo(
    () => (effOptional || []).map((f) => f.key).join("|"),
    [effOptional]
  );

  const sig = `${previewSig}__${requiredSig}__${optionalSig}`;
  const lastSigRef = useRef(sig);

  useEffect(() => {
    if (lastSigRef.current !== sig) {
      setCanonToSrc({});
      setSaved(false);
      lastSigRef.current = sig;
      setMtOpen(false);
      setMtSrc(null);
      setMtValues([]);
      setMtMap({});
      setErrMsg("");
    }
  }, [sig]);

  // Clear "saved" whenever user edits a slot
  useEffect(() => {
    setSaved(false);
    if (!canonToSrc[MESSAGE_TYPE_KEY]) {
      setMtOpen(false);
      setMtSrc(null);
      setMtValues([]);
      setMtMap({});
    }
  }, [canonToSrc]);

  const canonicalRows = useMemo(() => {
    const req = effRequired.map((f) => ({ ...f, required: true }));
    const opt = (effOptional || []).map((f) => ({ ...f, required: false }));
    return [...req, ...opt];
  }, [effRequired, effOptional]);

  // used/unassigned columns
  const used = useMemo(
    () => new Set(Object.values(canonToSrc).filter(Boolean)),
    [canonToSrc]
  );
  const unassigned = (preview?.headers || []).filter((h) => !used.has(h));

  const onDragStart = (e, name) => {
    e.dataTransfer.setData("text/plain", name);
    e.dataTransfer.effectAllowed = "move";
  };

  // helper: collect ALL distinct values from a user column
  const extractDistinctValues = (srcCol) => {
    const rows = preview?.rows || [];
    const seen = new Set();
    const out = [];
    for (const r of rows) {
      if (!r) continue;
      const raw = r[srcCol];
      const s = raw == null ? "" : String(raw);
      const key = s.trim();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(s);
      }
    }
    return out;
  };

  // Validate MT (returns {ok, vals})
  const validateMT = (src) => {
    const vals = extractDistinctValues(src);
    if (vals.length > MAX_MSGTYPE_DISTINCT) {
      return { ok: false, vals };
    }
    return { ok: true, vals };
  };

  const openMtModal = (src, vals) => {
    setMtSrc(src);
    setMtValues(vals);
    setMtOpen(true);
  };

  const onDropHeader = (e, canon) => {
    e.preventDefault();
    const src = e.dataTransfer.getData("text/plain");
    if (!src) return;

    if (canon === MESSAGE_TYPE_KEY) {
      const { ok, vals } = validateMT(src);
      if (!ok) {
        setErrMsg(
          `The selected column "${src}" has ${vals.length} distinct values. ` +
            `That suggests it might not be the Message Type column. ` +
            `Expected ≤ ${MAX_MSGTYPE_DISTINCT} (e.g., ${DEFAULT_MESSAGE_TYPES.join(", ")}).`
        );
        setMtOpen(false);
        setMtSrc(null);
        setMtValues([]);
        setMtMap({});
        return;
      }
      setErrMsg("");
      openMtModal(src, vals);
    } else {
      setErrMsg("");
    }

    setCanonToSrc((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) if (next[k] === src) next[k] = null;
      next[canon] = src;
      return next;
    });
  };

  const onClear = (canon) => setCanonToSrc((x) => ({ ...x, [canon]: null }));

  const allRequiredMapped = effRequired.every((r) => !!canonToSrc[r.key]);

  const handleSelect = (canon, value) => {
    if (canon === MESSAGE_TYPE_KEY && value) {
      const { ok, vals } = validateMT(value);
      if (!ok) {
        setErrMsg(
          `The selected column "${value}" has ${vals.length} distinct values. ` +
            `That suggests it might not be the Message Type column. ` +
            `Expected ≤ ${MAX_MSGTYPE_DISTINCT} (e.g., ${DEFAULT_MESSAGE_TYPES.join(", ")}).`
        );
        setMtOpen(false);
        setMtSrc(null);
        setMtValues([]);
        setMtMap({});
        return;
      }
      setErrMsg("");
      openMtModal(value, vals);
    } else {
      setErrMsg("");
    }

    setCanonToSrc((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (k !== canon && next[k] === value) next[k] = null;
      }
      next[canon] = value || null;
      return next;
    });
  };

  const confirm = async () => {
    const mappingSrcToCanon = {};
    for (const [canon, src] of Object.entries(canonToSrc)) {
      if (src) mappingSrcToCanon[src] = canon;
    }
    const options = {};
    if (canonToSrc[MESSAGE_TYPE_KEY] && Object.keys(mtMap || {}).length > 0) {
      options.valueMaps = { [MESSAGE_TYPE_KEY]: mtMap };
    }
    try {
      setSaving(true);
      await onConfirm?.(mappingSrcToCanon, options);
      setSaved(true);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4500);
    } catch (e) {
      const msg = e?.message || "Failed to apply mapping.";
      setErrMsg(msg);
      requestAnimationFrame(() =>
        errRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      );
      console.error("Confirm mapping failed:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* SR-only ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {saved ? "Mapping saved successfully" : ""}
      </div>

      {/* Title + dashed rule */}
      <div>
        <h2 className="text-2xl font-bold text-purple-900 tracking-tight">
          Mapping
        </h2>
        <div className="mt-2 w-full border-t-2 border-dashed border-orange-500" />
      </div>

      {/* Why mapping banner (theme-consistent) */}
      <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
        {MAPPING_WHY}
      </div>

      {/* Top actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded bg-white hover:bg-gray-50"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              className="text-purple-700"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                fill="none"
              />
              <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" />
              <line x1="12" y1="10" x2="12" y2="16" stroke="currentColor" />
            </svg>
            Schema & mapping guide
          </button>
        </div>

        <div className="flex items-center gap-3">
          {saved && (
            <span className="inline-flex items-center gap-2 rounded bg-green-50 text-green-800 border border-green-300 px-2 py-1 text-xs">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10Zm-1.1-6.1-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4-6 6Z"
                  fill="currentColor"
                />
              </svg>
              Mapping saved
            </span>
          )}
          <button
            type="button"
            onClick={() => setDataOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded bg-white hover:bg-gray-50"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              className="text-purple-700"
            >
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                ry="2"
                stroke="currentColor"
                fill="none"
              />
              <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" />
              <line x1="9" y1="5" x2="9" y2="19" stroke="currentColor" />
              <line x1="15" y1="5" x2="15" y2="19" stroke="currentColor" />
            </svg>
            View your uploaded data
          </button>
        </div>
      </div>

      {/* Mapping table + chip bank */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Mapping table */}
        <div className="md:col-span-2">
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-purple-50 px-4 py-2 border-b">
              <p className="text-sm text-purple-900">
                Map your columns to required fields (
                <span className="text-red-600">*</span> mandatory)
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b w-1/3">
                      Canonical field
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      Match (drop here or pick)
                    </th>
                    <th className="px-3 py-2 text-left border-b w-28">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {canonicalRows.map((f) => {
                    const assigned = canonToSrc[f.key] || null;
                    const isMsgType = f.key === MESSAGE_TYPE_KEY;
                    return (
                      <tr key={f.key} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-center gap-2">
                            {f.required && (
                              <span className="text-red-600">*</span>
                            )}
                            <span className="font-medium text-gray-900">
                              {f.key}
                            </span>
                          </div>
                        </td>
                        <td
                          className="px-3 py-2"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => onDropHeader(e, f.key)}
                        >
                          <div className="flex items-center gap-2">
                            {assigned ? (
                              <span
                                className={`px-2 py-1 border rounded bg-white ${isMsgType ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
                                title={isMsgType ? "Click to edit Message Type value mapping" : assigned}
                                onClick={() => {
                                  if (isMsgType) {
                                    const vals = extractDistinctValues(assigned);
                                    if (vals.length > MAX_MSGTYPE_DISTINCT) {
                                      setErrMsg(
                                        `The selected column "${assigned}" has ${vals.length} distinct values. ` +
                                          `That suggests it might not be the Message Type column. ` +
                                          `Expected ≤ ${MAX_MSGTYPE_DISTINCT} (e.g., ${DEFAULT_MESSAGE_TYPES.join(", ")}).`
                                      );
                                      return;
                                    }
                                    setErrMsg("");
                                    openMtModal(assigned, vals);
                                  }
                                }}
                              >
                                {assigned}
                                {isMsgType && " • edit values"}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500">
                                drop a column here
                              </span>
                            )}
                            {/* fallback select */}
                            <select
                              className="px-2 py-1 border rounded bg-white text-sm"
                              value={assigned || ""}
                              onChange={(e) =>
                                handleSelect(f.key, e.target.value)
                              }
                            >
                              <option value="">— select —</option>
                              {assigned && !unassigned.includes(assigned) && (
                                <option value={assigned}>{assigned}</option>
                              )}
                              {unassigned.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => onClear(f.key)}
                            className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                          >
                            Clear
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {errMsg && (
              <div ref={errRef}>
                <ErrorCallout message={errMsg} onClose={() => setErrMsg("")} />
              </div>
            )}
            <div className="px-4 py-3 border-t flex items-center justify-between">
              {!allRequiredMapped && (
                <span className="text-xs text-red-600">
                  Map all required fields and Confirm Mapping to continue.
                </span>
              )}
              <button
                disabled={!allRequiredMapped || saving}
                onClick={confirm}
                className={`px-4 py-2 rounded text-white inline-flex items-center gap-2 ${
                  !allRequiredMapped || saving
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {saving ? (
                  <>
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Saving…
                  </>
                ) : saved ? (
                  <>
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10Zm-1.1-6.1-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4-6 6Z"
                        fill="currentColor"
                      />
                    </svg>
                    Saved
                  </>
                ) : (
                  "Confirm Mapping"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Your columns (chip bank) */}
        <div>
          <div className="border rounded-lg p-3 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              Your columns
            </h4>
            <p className="text-xs text-gray-500 mt-2 mb-2">
              Drag a chip onto a field row, or use the dropdown.
            </p>
            <hr className="mb-2" />
            <div className="flex flex-wrap gap-2 min-h-[56px]">
              {unassigned.length > 0 ? (
                unassigned.map((h) => (
                  <HeaderChip key={h} name={h} onDragStart={onDragStart} />
                ))
              ) : (
                <span className="text-xs text-gray-500">
                  all columns assigned
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info modal (table) */}
      {infoOpen && (
        <Modal
          title="All columns: requirements & examples"
          onClose={() => setInfoOpen(false)}
        >
          <div className="mb-3 rounded bg-red-50 border border-red-200 p-2 text-xs text-red-900">
            {MAPPING_WHY}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border-b">Field</th>
                  <th className="px-3 py-2 text-left border-b">Required</th>
                  <th className="px-3 py-2 text-left border-b">Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...effRequired.map((f) => ({ ...f, required: true })),
                  ...(effOptional || []).map((f) => ({ ...f, required: false })),
                ].map((f) => (
                  <tr key={f.key} className="odd:bg-white even:bg-gray-50">
                    <td className="px-3 py-2 border-b font-medium text-gray-900">
                      {f.key}
                    </td>
                    <td className="px-3 py-2 border-b">
                      {f.required ? (
                        <span className="text-red-600">*</span>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="px-3 py-2 border-b text-gray-700">
                      {f.desc}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Data modal (full sample table) */}
      {dataOpen && (
        <Modal title="Sample data preview" onClose={() => setDataOpen(false)}>
          {!preview ? (
            <p className="text-sm text-gray-600">No preview available.</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {preview.headers.map((h) => (
                      <th key={h} className="px-3 py-2 text-left border-b">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).slice(0, PREVIEW_ROWS).map((r, i) => (
                    <tr key={i} className="odd:bg-white even:bg-gray-50">
                      {preview.headers.map((h) => (
                        <td key={h} className="px-3 py-2 border-b align-top">
                          {r?.[h] == null ? "" : String(r[h])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}                           
        </Modal>
      )}

      {/* Message Type value mapping modal */}
      <ValueMapModal
        open={mtOpen}
        onClose={() => setMtOpen(false)}
        srcCol={mtSrc}
        values={mtValues}
        displayChoices={DEFAULT_MESSAGE_TYPES}
        canonicalChoices={CANON_MESSAGE_TYPES}
        map={mtMap}
        setMap={setMtMap}
      />

      {/* Success toast */}
      <Toast show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
}
